import { Component, OnInit, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { AuthorizationService } from '@src/app/helpers/authorization.service';
import { TimeregistrationService } from '@src/app/helpers/timeregistration/timeregistration.service';
import { formatMinutesAsHours } from '@src/app/helpers/timeregistration/hours.util';
import { TimeReportEntry } from '@src/app/models/timereportentry';

interface TotalRow {
  key: string;
  label: string;
  minutes: number;
  percent: number;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-timereport',
  templateUrl: './timereport.component.html',
  styleUrls: ['./timereport.component.css'],
})
export class TimereportComponent implements OnInit {
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthorizationService);
  private readonly timeregSrv = inject(TimeregistrationService);

  timeregEnabled = false;
  canSeeTeam = false;
  activeTab: 'my' | 'team' = 'my';

  loading = false;
  errorMessage: string;
  entries: TimeReportEntry[] = [];

  fromDate: string;
  toDate: string;
  filterCustomerId = '';
  filterProjectId = '';

  sortCol: 'workDate' | 'customerName' | 'projectTitle' | 'taskTitle' | 'minutes' = 'workDate';
  sortDir = -1;

  ngOnInit() {
    this.meta.addTags([
      { name: 'robots', content: 'noindex' },
      { name: 'googlebot', content: 'noindex' },
    ]);

    this.timeregEnabled = SessionHelper.hasModule('timereg') && !SessionHelper.getCustomer();
    if (!this.timeregEnabled) {
      this.router.navigate(['/portal']);
      return;
    }

    this.canSeeTeam = this.auth.isAuthorized('orgedit', SessionHelper.getChoosenOrg());

    const today = new Date();
    this.fromDate = this.toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    this.toDate = this.toIsoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));

    this.load();
  }

  private toIsoDate(d: Date): string {
    return d.toISOString().substring(0, 10);
  }

  setTab(tab: 'my' | 'team') {
    if (tab === 'team' && !this.canSeeTeam) {
      return;
    }
    this.activeTab = tab;
    this.filterCustomerId = '';
    this.filterProjectId = '';
    this.load();
  }

  load() {
    this.loading = true;
    this.errorMessage = undefined;

    const filters = {
      from: this.fromDate || undefined,
      to: this.toDate || undefined,
      customerId: this.activeTab === 'team' ? (this.filterCustomerId || undefined) : undefined,
      projectId: this.activeTab === 'team' ? (this.filterProjectId || undefined) : undefined,
      personId: this.activeTab === 'my' ? SessionHelper.getUserSub() : undefined,
    };

    this.timeregSrv.getOrgTimeReport(filters)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (entries) => {
          this.entries = entries || [];
          this.loading = false;
        },
        error: (err) => {
          console.error('[Timereport] Failed to load time report', err);
          this.errorMessage = "Couldn't load the time report.";
          this.loading = false;
        },
      });
  }

  clearFilters() {
    const today = new Date();
    this.fromDate = this.toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    this.toDate = this.toIsoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    this.filterCustomerId = '';
    this.filterProjectId = '';
    this.load();
  }

  sortBy(col: typeof this.sortCol) {
    if (this.sortCol === col) {
      this.sortDir *= -1;
    } else {
      this.sortCol = col;
      this.sortDir = 1;
    }
  }

  sortedEntries(): TimeReportEntry[] {
    const col = this.sortCol;
    const dir = this.sortDir;
    return [...this.entries].sort((a, b) => {
      const av = (col === 'minutes' ? a.minutes : (a[col] || '')) as any;
      const bv = (col === 'minutes' ? b.minutes : (b[col] || '')) as any;
      if (av < bv) { return -dir; }
      if (av > bv) { return dir; }
      return 0;
    });
  }

  totalMinutes(): number {
    return this.entries.reduce((sum, e) => sum + (e.minutes || 0), 0);
  }

  // Distinct customers/projects across the loaded entries, for the Team
  // tab's filter dropdowns — derived from the data itself rather than a
  // separate lookup, so the dropdowns only ever offer what's actually there.
  customerOptions(): { id: string; name: string }[] {
    const byId = new Map<string, string>();
    this.entries.forEach((e) => {
      if (e.customerId) { byId.set(e.customerId, e.customerName || e.customerId); }
    });
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }

  projectOptions(): { id: string; title: string }[] {
    const byId = new Map<string, string>();
    this.entries.forEach((e) => {
      if (e.projectId) { byId.set(e.projectId, e.projectTitle || e.projectId); }
    });
    return Array.from(byId, ([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title));
  }

  private groupBy(keyFn: (e: TimeReportEntry) => string, labelFn: (e: TimeReportEntry) => string): TotalRow[] {
    const totals = new Map<string, TotalRow>();
    this.entries.forEach((e) => {
      const key = keyFn(e) || 'unassigned';
      const label = labelFn(e) || 'Unassigned';
      if (!totals.has(key)) {
        totals.set(key, { key, label, minutes: 0, percent: 0 });
      }
      totals.get(key).minutes += e.minutes || 0;
    });
    const total = this.totalMinutes();
    const rows = Array.from(totals.values());
    rows.forEach((r) => { r.percent = total ? Math.round((r.minutes / total) * 100) : 0; });
    return rows.sort((a, b) => b.minutes - a.minutes);
  }

  byCustomer(): TotalRow[] {
    return this.groupBy((e) => e.customerId, (e) => e.customerName || e.customerId);
  }

  byProject(): TotalRow[] {
    return this.groupBy((e) => e.projectId, (e) => e.projectTitle || e.projectId);
  }

  byPerson(): TotalRow[] {
    return this.groupBy((e) => e.personId, (e) => e.personId);
  }

  hours(minutes: number): string {
    return formatMinutesAsHours(minutes, ',');
  }

  exportCsv() {
    const rows = [['Date', 'Customer', 'Project', 'Task', 'Hours', 'Notes']];
    this.sortedEntries().forEach((e) => {
      rows.push([
        e.workDate,
        e.customerName || '',
        e.projectTitle || '',
        e.taskTitle || '',
        (Math.round((e.minutes / 60) * 100) / 100).toString(),
        e.note || '',
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `time-report_${this.fromDate}_${this.toDate}.csv`;
    a.click();
  }

  print() {
    window.print();
  }
}
